// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import api from '../utils/api';
import { dateTime } from '../utils/dates';
import { errorMessage } from '../utils/erreurs';

/**
 * Prospection des établissements (demande de Romain du 22/10/2026) : entre
 * deux appels, une adresse tapée et Entrée, la présentation part depuis la
 * boîte de Romain avec la plaquette jointe (POST /api/prospection/envoi,
 * services/pro/prospection.py). Le texte est fixe et se lit dans l'aperçu.
 *
 * Une adresse déjà servie est refusée (409) : la page demande alors une
 * confirmation avant de renvoyer.
 */
export default function ProspectionPage() {
  const [email, setEmail] = useState('');
  const [etat, setEtat] = useState(null);
  const [apercu, setApercu] = useState('');
  const [voirApercu, setVoirApercu] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [doublon, setDoublon] = useState('');
  const [message, setMessage] = useState(null);
  const champ = useRef(null);

  const charger = useCallback(async () => {
    try {
      const res = await api.get('/prospection/');
      setEtat(res.data);
    } catch (e) {
      setMessage({ severity: 'error', text: errorMessage(e, 'Impossible de charger les envois') });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirApercu = async () => {
    if (!apercu) {
      try {
        const res = await api.get('/prospection/apercu', { responseType: 'text' });
        setApercu(res.data);
      } catch (e) {
        setMessage({ severity: 'error', text: errorMessage(e, "Impossible de charger l'aperçu") });
        return;
      }
    }
    setVoirApercu((v) => !v);
  };

  const envoyer = async (renvoyer = false) => {
    const adresse = email.trim();
    if (!adresse || envoi) return;
    setEnvoi(true);
    setMessage(null);
    try {
      await api.post('/prospection/envoi', { email: adresse, renvoyer });
      setMessage({ severity: 'success', text: `Présentation envoyée à ${adresse}.` });
      setEmail('');
      setDoublon('');
      charger();
    } catch (e) {
      if (e?.response?.status === 409) {
        setDoublon(errorMessage(e, 'Déjà envoyée'));
      } else {
        setMessage({ severity: 'error', text: errorMessage(e, "L'envoi a échoué") });
      }
    } finally {
      setEnvoi(false);
      champ.current?.focus();
    }
  };

  const envois = etat?.envois || [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>Prospection des établissements</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        L’adresse de l’établissement, puis Entrée : la présentation part depuis
        romain.dellamonica@vigeos.fr, avec la plaquette en pièce jointe.
      </Typography>

      {etat && !etat.configuree && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Boîte d’envoi non configurée : PROSPECTION_SMTP_USER et PROSPECTION_SMTP_PASSWORD
          (mot de passe d’application Google) manquent dans l’environnement du backend.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          component="form"
          onSubmit={(ev) => { ev.preventDefault(); envoyer(false); }}
          sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            inputRef={champ}
            autoFocus
            type="email"
            label="Adresse de l’établissement"
            placeholder="direction@ehpad-exemple.fr"
            value={email}
            onChange={(ev) => { setEmail(ev.target.value); setDoublon(''); }}
            sx={{ flex: 1, minWidth: 260 }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!email.trim() || envoi}
            startIcon={envoi ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          >
            Envoyer
          </Button>
        </Box>
        <Collapse in={!!doublon}>
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            action={(
              <Button color="inherit" size="small" disabled={envoi} onClick={() => envoyer(true)}>
                Renvoyer quand même
              </Button>
            )}
          >
            {doublon}
          </Alert>
        </Collapse>
        {message && <Alert severity={message.severity} sx={{ mt: 2 }}>{message.text}</Alert>}
      </Paper>

      <Button onClick={ouvrirApercu} sx={{ mb: 1 }}>
        {voirApercu ? 'Masquer l’aperçu' : 'Voir le courriel envoyé'}
      </Button>
      <Collapse in={voirApercu}>
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <iframe
            title="Aperçu du courriel"
            srcDoc={apercu}
            sandbox=""
            style={{ width: '100%', height: 900, border: 0, display: 'block' }}
          />
        </Paper>
      </Collapse>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Envois ({envois.length})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Adresse</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {envois.map((e, i) => (
              <TableRow key={`${e.email}-${e.date}-${i}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(e.date, '')}</TableCell>
                <TableCell>{e.email}</TableCell>
              </TableRow>
            ))}
            {!envois.length && (
              <TableRow>
                <TableCell colSpan={2} sx={{ color: 'text.secondary' }}>Aucun envoi pour l’instant.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
